/**
 * Coach Mark — single in-match tutorial popup, mobile-first.
 *
 * Phase 3.5.7 P0: switched from `absolute` (which got clipped by the
 * GameScreen container's overflow-hidden / h-[100dvh] on mobile) to
 * `fixed` viewport positioning. Always renders inside the visible
 * viewport, never below the mobile browser chrome / nav bar, and
 * never offscreen. Body content scrolls internally if it ever grows
 * past 40dvh.
 *
 * Per PRD: max 4 auto-fired marks per match. The HELP button stays
 * available beyond that cap.
 */
import React from 'react';
import { motion } from 'framer-motion';

export default function CoachMark({ mark, onNext, onSkip, primary = 'Got it' }) {
  if (!mark) return null;
  return (
    <motion.div
      data-testid="tutorial-coach-mark"
      data-coachid={mark.id}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.28 }}
      className="fixed z-[60]"
      style={{
        left: '12px',
        right: '12px',
        bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <div
        className="mx-auto w-full max-w-[420px] rounded-xl border border-[hsl(var(--k-gold)/0.4)] bg-black/90 backdrop-blur-md shadow-[0_10px_28px_rgba(0,0,0,0.65)] flex flex-col"
        style={{ maxHeight: '45dvh' }}
      >
        <div className="p-4 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
          <div className="font-heading text-[11px] tracking-[0.3em] text-[hsl(var(--k-gold-bright))] mb-1.5 uppercase">
            {mark.title}
          </div>
          <div className="space-y-1.5">
            {mark.body.map((line, idx) => (
              <div key={idx} className="font-body text-sm text-white/85 leading-snug">
                {line}
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 px-3 pb-3 pt-1 border-t border-white/8">
          <button
            data-testid="coach-mark-skip"
            onClick={onSkip}
            className="font-heading text-[10px] tracking-[0.28em] text-white/55 hover:text-white/85 px-2 py-2"
          >
            SKIP TUTORIAL
          </button>
          <button
            data-testid="coach-mark-next"
            onClick={onNext}
            className="cta-pill cta-pill--primary px-4 py-2 text-xs"
          >
            {primary}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
